/**
 * a2-29 — the Year header's first line: where Doug is, for how much longer,
 * and what comes next.
 *
 * Doug chose: "Berlin 6 more days · next Wroclaw, Fri 9 Oct", and when away
 * "Wroclaw 2 more days · back Sun 11 Oct". The whole-year totals the header
 * used to lead with answer a question asked at tax time; this one is asked
 * every time the tab opens.
 *
 * "More days" counts the days after today to the period's last day, the
 * figure `PeriodCard`'s "6 days left" already printed: Berlin until Thu 8
 * Oct, on Fri 2 Oct, is 6. "Back" is the last day away — Doug's own example
 * reads "back Sun 11 Oct" for a trip whose stay ends Sun 11 Oct. When the
 * period after an away one is another away one (Vilnius straight on to
 * the second home), there is no "back": it says "then Ireland" and its first day.
 *
 * Dependency-free apart from `calendar.ts` (itself only type imports), so
 * `test/year-ux.test.ts` drives it.
 */
import { fmt, type Period } from './calendar.ts'
import { COUNTRIES } from './placeEmoji.ts'

const DAY_MS = 86_400_000
const daysBetween = (a: string, b: string) => Math.round((Date.parse(`${b}T00:00:00Z`) - Date.parse(`${a}T00:00:00Z`)) / DAY_MS)

/** "Fri 9 Oct", and the year only when it is not this one. */
export function shortDay(on: string, today: string): string {
  const o: Intl.DateTimeFormatOptions = on.slice(0, 4) === today.slice(0, 4)
    ? { weekday: 'short', day: 'numeric', month: 'short' }
    : { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' }
  return fmt(on, o).replace(/,/g, '')
}

/** What a period is called on the line: `home` (the home region's name, for
 *  the `'Home'` token — trap T1), the trip's own label, else
 *  the profile's second-home label, else the country it was given, else null
 *  (a trip with no name). `second` is `{ label }` from the profile, or null. */
export function periodName(
  p: Pick<Period, 'place' | 'label' | 'country'>,
  second: { label: string } | null | undefined,
  home: string,
): string | null {
  if (p.place === 'Home') return home
  if (p.label?.trim()) return p.label.trim()
  if (p.place === 'Home2') return second?.label.trim() || 'Second home'
  const country = p.country ? COUNTRIES.find(([c]) => c === p.country)?.[1] : undefined
  return country ?? null
}

export interface NowLine {
  /** Where Doug is: "Berlin" (the home region's name), "Wroclaw", "Away" for a trip with no name. */
  here: string
  /** "6 more days", "1 more day", "last day". */
  left: string
  /** "next Wroclaw, Fri 9 Oct", "back Sun 11 Oct", "then Ireland, Thu 26
   *  Mar" — or "no trips planned" at home with nothing after it. */
  after: string
}

/**
 * The line for `today`, from periods that cover it (`Year.tsx`'s
 * `displayPeriods`, today through the end of next year). Null when no period
 * covers today — a read not landed yet, never a real state.
 */
export function nowLine(
  periods: readonly Period[], today: string, second: { label: string } | null | undefined, home: string,
): NowLine | null {
  const i = periods.findIndex(p => today >= p.start && today <= p.end)
  if (i < 0) return null
  const p = periods[i]
  const next = periods[i + 1]
  const more = daysBetween(today, p.end)
  const left = more <= 0 ? 'last day' : `${more} more ${more === 1 ? 'day' : 'days'}`
  const here = periodName(p, second, home) ?? 'Away'

  let after: string
  if (p.place === 'Home') {
    // `periods()` stops at the window's end, so a home period with nothing
    // after it is home to the horizon: there is no next trip to name.
    after = next ? `next ${periodName(next, second, home) ?? 'trip'}, ${shortDay(next.start, today)}` : 'no trips planned'
  } else if (!next || next.place === 'Home') {
    after = `back ${shortDay(p.end, today)}`
  } else {
    after = `then ${periodName(next, second, home) ?? 'a trip'}, ${shortDay(next.start, today)}`
  }
  return { here, left, after }
}
